import { useCallback, useEffect, useState } from "react";
import { FileText, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../../lib/api";
import { StatusBadge } from "../StatusBadge";
import { Button } from "../ui/button";
import { PrintSettings, useTrims, defaultPrint } from "./print/PrintSettings";
import { CalcPanel, useCalc } from "./print/CalcPanel";
import { CoverForm } from "./print/CoverForm";
import { OutputCard } from "./print/OutputList";
import { BuiltNext } from "./StageNext";

export default function PrintTab({ book, reload, edition }) {
  const trims = useTrims();
  const pic = !book.approved_version_id && book.picture_page_count && book.picture_edition === edition;
  const [f, setF] = useState(() => (pic ? { ...defaultPrint(edition), trim: book.picture_trim || defaultPrint(edition).trim, paper: book.picture_paper || defaultPrint(edition).paper } : defaultPrint(edition)));
  const [outs, setOuts] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/books/${book.id}/print/${edition}`).then((r) => { setOuts(r.data); setSel((s) => s || r.data[0] || null); }), [book.id, edition]);
  useEffect(() => { load(); }, [load]);
  const calc = useCalc(book.id, f, outs.length);
  const ed = book.editions?.[edition] || {};
  const done = (o) => { setSel(o); load(); reload(); };
  const run = async (kind) => {
    setBusy(kind);
    try {
      const { data } = kind === "docx" ? await api.post(`/books/${book.id}/formatted-docx`, f) : await api.post(`/books/${book.id}/print/interior`, f);
      toast.success(`${kind === "docx" ? "Formatted DOCX" : "Interior PDF"} ready — ${data.report.overall}`); kind === "docx" ? setSel(data) : done(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="grid xl:grid-cols-[380px_1fr] gap-6" data-testid={`${edition}-tab`}>
      <BuiltNext book={book} edition={edition} />
      <div className="space-y-4">
        <div className="panel p-5 space-y-3">
          <p className="eyebrow">{edition === "hardcover" ? "Hardcover interior" : "Paperback interior"}</p>
          <p className="text-sm text-slate-400" data-testid={`${edition}-source`}>{book.approved_version_id ? "Built from your approved manuscript version." : pic ? `Picture book: the cover uses your ${book.picture_page_count}-page picture interior (${book.picture_trim}", ${book.picture_paper} paper).` : "Approve a version in the Versions tab first."}{ed.outdated && <span className="text-warn-300"> Manuscript changed — regenerate.</span>}</p>
          <PrintSettings edition={edition} f={f} setF={setF} trims={trims} />
          <Button onClick={() => run("pdf")} disabled={!!busy || !book.approved_version_id} className="w-full bg-amber-600 hover:bg-amber-500" data-testid={`${edition}-generate-interior`}><BookOpen size={14} className="mr-1" />{busy === "pdf" ? "Typesetting & validating…" : "Generate print-ready interior PDF"}</Button>
          <Button variant="outline" onClick={() => run("docx")} disabled={!!busy || !book.approved_version_id} className="w-full border-slate-700" data-testid={`${edition}-generate-docx`}><FileText size={14} className="mr-1" />{busy === "docx" ? "Formatting…" : "Formatted DOCX (same trim & margins)"}</Button>
        </div>
        <CoverForm book={book} edition={edition} f={f} onDone={done} calc={calc && !calc.error ? calc : null} />
      </div>
      <div className="space-y-4 min-w-0">
        <CalcPanel c={calc} edition={edition} />
        {sel && <OutputCard key={sel.id} o={sel} />}
        <div className="panel p-2" data-testid={`${edition}-outputs`}>
          <p className="eyebrow p-3">History (every file kept)</p>
          {outs.map((o) => (
            <button key={o.id} onClick={() => setSel(o)} className={`w-full text-left p-3 rounded-lg ${sel?.id === o.id ? "bg-slate-800" : "hover:bg-slate-800/50"}`} data-testid={`${edition}-output-${o.id}`}>
              <div className="flex justify-between gap-2"><span className="text-xs font-mono truncate">{o.filename}</span><StatusBadge status={o.report.overall} /></div>
              <p className="text-[11px] text-slate-500 mt-1">v{o.version_number}{o.page_count ? ` · ${o.page_count} pp` : ""} · {fmtDate(o.created_at)}</p>
            </button>
          ))}
          {!outs.length && <p className="p-3 text-sm text-slate-500">Nothing generated yet.</p>}
        </div>
        <p className="text-xs text-slate-500">Gutter follows KDP's page-count table and is recalculated from the real typeset page count. Fonts are embedded. Hardcover wrap/hinge and dust-jacket values are standard estimates — confirm with your printer's cover calculator (marked NOT VERIFIED).</p>
      </div>
    </div>
  );
}
